import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parseISO, startOfDay } from "date-fns";
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  XAxis,
  YAxis,
} from "recharts";
import { useSalesData } from "contexts/SalesDataContext";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "components/ui/chart";
import {
  bundleFormatColor,
  ColorDot,
  cursorTooltipWrapperProps,
  estimateFormatTooltipWidth,
  GranularityToggle,
  renderCursorTooltip,
  type Granularity,
} from "components/charts";
import { Text } from "components/text";
import type { AmazonSalesBucket, AmazonSalesResponse } from "reedy-data/api";
import {
  formatBucketAxisLabel,
  formatCompactNumber,
  formatPercent,
} from "utils/formatting";
import { getEffectiveWindow } from "../../utils/effectiveWindow";
import { mapAmazonFormatResponse } from "../../data/mapAmazonResponse";
import type { FormatMeta } from "../../data/mapSalesResponse";
import { EarliestDataPill } from "../EarliestDataPill";
import { AmazonSalesFooter } from "./AmazonSalesFooter";
import { AmazonSalesTooltip } from "./AmazonSalesTooltip";
import { AmazonSalesFormatTooltip } from "./AmazonSalesFormatTooltip";
import { AmazonSalesChartPanel } from "./AmazonSalesChartPanel";
import { MetadataEventOverlay } from "../events";
import { withEmptyEventDays } from "../../data/withEmptyEventDays";
import type { MetadataEvent } from "../../hooks/useMetadataEvents";

const ANGLED_LABEL_BAR_THRESHOLD = 12;

const isPreorderDate = (isoDate: string, publishedAt?: Date | null): boolean =>
  publishedAt != null &&
  startOfDay(parseISO(isoDate)) < startOfDay(publishedAt);

export const AmazonSalesChart = ({
  sales,
  formats,
  granularity,
  publishedAt,
  availableGranularities,
  onGranularityChange,
  events = [],
  className,
}: {
  sales: AmazonSalesResponse;
  /** Bundle formats to stack; segment i gets purple shade i. */
  formats?: FormatMeta[];
  granularity: Granularity;
  publishedAt?: Date | null;
  availableGranularities: Granularity[];
  onGranularityChange: (granularity: Granularity) => void;
  events?: MetadataEvent[];
  className?: string;
}) => {
  const { t } = useTranslation(["productDetails"]);
  const { from, to, firstAmazonSaleDate } = useSalesData();

  const handleDrill = useMemo(() => {
    const granularityIndex = availableGranularities.indexOf(granularity);
    const finerGranularity =
      granularityIndex > 0
        ? availableGranularities[granularityIndex - 1]
        : undefined;
    return finerGranularity
      ? () => onGranularityChange(finerGranularity)
      : undefined;
  }, [granularity, availableGranularities, onGranularityChange]);

  const isByFormat = (formats?.length ?? 0) > 0;

  // Flatten per-format units for stacked bars; keep aggregate fields for overlay lines.
  const formatViewModel = useMemo(
    () =>
      isByFormat && formats ? mapAmazonFormatResponse(sales, formats) : null,
    [isByFormat, sales, formats],
  );
  // Widen to Record<string, unknown> so aggregate and per-format datums share one prop type.
  const chartRows: { date: string }[] = formatViewModel?.data ?? sales.buckets;
  const chartData = useMemo(
    () =>
      granularity === "daily"
        ? withEmptyEventDays(chartRows, events)
        : chartRows,
    [chartRows, events, granularity],
  );

  const effectiveWindow = getEffectiveWindow({
    firstDataDate: firstAmazonSaleDate,
    publishedAt,
    selectedStart: from,
    selectedEnd: to,
  });

  const angledLabels = chartData.length > ANGLED_LABEL_BAR_THRESHOLD;

  const hasPreorders = useMemo(
    () =>
      publishedAt != null &&
      sales.buckets.some((bucket) => isPreorderDate(bucket.date, publishedAt)),
    [sales.buckets, publishedAt],
  );

  const config = useMemo<ChartConfig>(
    () => ({
      units: {
        label: t("productDetails:salesData.amazonSales.tooltip.units"),
        color: "var(--chart-6)",
      },
      unitsPreorder: {
        label: t("productDetails:salesData.amazonSales.legend.preorders"),
        color: "var(--chart-6-muted)",
      },
      conversionRate: {
        label: t("productDetails:salesData.amazonSales.tooltip.conversion"),
        color: "var(--chart-1)",
      },
      salesRank: {
        label: t("productDetails:salesData.amazonSales.tooltip.rank"),
        color: "var(--chart-3)",
      },
    }),
    [t],
  );

  // A [0, max] domain would flatten real-world ranks against the chart edge.
  const rankDomain = useMemo<[number, number] | undefined>(() => {
    const ranks = sales.buckets
      .map((bucket) => bucket.salesRank)
      .filter((rank): rank is number => rank != null);
    if (ranks.length === 0) {
      return undefined;
    }
    const min = Math.min(...ranks);
    const max = Math.max(...ranks);
    const cushion = Math.max(1, (max - min) * 0.18);
    return [Math.max(0, min - cushion), max + cushion];
  }, [sales.buckets]);

  const share = sales.summary.amazonShareOfTotalUnits;
  const subtitle =
    share != null
      ? t("productDetails:salesData.amazonSales.subtitleShare", {
          share: formatPercent(share),
        })
      : t("productDetails:salesData.amazonSales.subtitle");

  const renderUnitsBar = ({ x, y, width, height, payload }: BarShapeProps) => {
    const date = (payload as AmazonSalesBucket | undefined)?.date;
    const preorder = date != null && isPreorderDate(date, publishedAt);
    return (
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        radius={[4, 4, 0, 0]}
        fill={preorder ? "var(--color-unitsPreorder)" : "var(--color-units)"}
      />
    );
  };

  const unitsLegendItems = hasPreorders ? (
    <>
      <span role="listitem" className="flex items-center gap-1.5">
        <ColorDot className="h-3 w-3 bg-(--chart-6-muted)" />
        <Text as="span" variant="metricCaption">
          {t("productDetails:salesData.amazonSales.legend.preorders")}
        </Text>
      </span>
      <span role="listitem" className="flex items-center gap-1.5">
        <ColorDot className="h-3 w-3 bg-(--chart-6)" />
        <Text as="span" variant="metricCaption">
          {t("productDetails:salesData.amazonSales.legend.sales")}
        </Text>
      </span>
    </>
  ) : (
    <span role="listitem" className="flex items-center gap-1.5">
      <ColorDot className="h-3 w-3 bg-(--chart-6)" />
      <Text as="span" variant="metricCaption">
        {t("productDetails:salesData.amazonSales.tooltip.units")}
      </Text>
    </span>
  );

  return (
    <AmazonSalesChartPanel
      titleBadge={
        effectiveWindow?.dataStartsInWindow ? (
          <EarliestDataPill date={effectiveWindow.earliestDataDate} />
        ) : null
      }
      subtitle={subtitle}
      actions={
        <GranularityToggle
          value={granularity}
          availableGranularities={availableGranularities}
          onChange={onGranularityChange}
        />
      }
      legend={
        <div
          role="list"
          aria-label={t(
            isByFormat
              ? "productDetails:salesData.amazonSales.legendFormatsAriaLabel"
              : "productDetails:salesData.amazonSales.legendAriaLabel",
          )}
          className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground"
        >
          {isByFormat && formatViewModel
            ? formatViewModel.formats.map((format, index) => (
                <span
                  key={format.isbn}
                  role="listitem"
                  className="flex items-center gap-1.5"
                >
                  <ColorDot
                    className="h-3 w-3"
                    style={{ backgroundColor: bundleFormatColor(index) }}
                  />
                  <Text as="span" variant="metricCaption">
                    {format.label}
                  </Text>
                </span>
              ))
            : unitsLegendItems}
          <span role="listitem" className="flex items-center gap-1.5">
            <span aria-hidden className="h-0.5 w-5 bg-(--chart-1)" />
            <Text as="span" variant="metricCaption">
              {t("productDetails:salesData.amazonSales.tooltip.conversion")}
            </Text>
          </span>
          <span role="listitem" className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="w-5 border-t-2 border-dashed border-(--chart-3)"
            />
            <Text as="span" variant="metricCaption">
              {t("productDetails:salesData.amazonSales.tooltip.rank")}
            </Text>
          </span>
        </div>
      }
      footer={<AmazonSalesFooter summary={sales.summary} />}
      className={className}
    >
      <ChartContainer config={config} className="aspect-auto h-[320px] w-full">
        <ComposedChart
          data={chartData}
          margin={{ top: 60, right: 4, bottom: 0, left: 4 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={16}
            angle={angledLabels ? -45 : 0}
            textAnchor={angledLabels ? "end" : "middle"}
            height={angledLabels ? 56 : 30}
            tickFormatter={(value) =>
              formatBucketAxisLabel(String(value), granularity)
            }
          />
          <YAxis
            yAxisId="units"
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value) => formatCompactNumber(Number(value))}
          />
          <YAxis
            yAxisId="conversion"
            orientation="right"
            tickLine={false}
            axisLine={false}
            width={44}
            tickFormatter={(value) => formatPercent(Number(value))}
          />
          {/* Inverted rank: lower (better) plots higher; width=0 avoids pushing units labels off-canvas. */}
          <YAxis yAxisId="rank" hide reversed domain={rankDomain} width={0} />
          <ChartTooltip
            cursor={false}
            {...(isByFormat ? cursorTooltipWrapperProps : {})}
            content={(props) => {
              if (isByFormat && formatViewModel) {
                return renderCursorTooltip(
                  <AmazonSalesFormatTooltip
                    formats={formatViewModel.formats}
                    currency={sales.currency}
                  />,
                  props,
                  {
                    estimatedWidth: estimateFormatTooltipWidth(
                      formatViewModel.formats.length,
                    ),
                  },
                );
              }
              const { active, payload } = props;
              if (!active || !payload?.length) {
                return null;
              }
              const bucket = payload[0].payload as AmazonSalesBucket;
              return (
                <AmazonSalesTooltip
                  {...bucket}
                  currency={sales.currency}
                  granularity={granularity}
                  isPreorder={isPreorderDate(bucket.date, publishedAt)}
                />
              );
            }}
          />
          {isByFormat && formatViewModel ? (
            formatViewModel.formats.map((format, index) => (
              <Bar
                key={format.isbn}
                yAxisId="units"
                dataKey={format.isbn}
                stackId="amazon-format"
                fill={bundleFormatColor(index)}
                radius={
                  index === formatViewModel.formats.length - 1
                    ? [4, 4, 0, 0]
                    : undefined
                }
              />
            ))
          ) : (
            <Bar
              yAxisId="units"
              dataKey="units"
              radius={[4, 4, 0, 0]}
              shape={renderUnitsBar}
            />
          )}
          {/* No connectNulls: conversion breaks across buckets with missing data. */}
          <Line
            yAxisId="conversion"
            dataKey="conversionRate"
            stroke="var(--color-conversionRate)"
            strokeWidth={2}
            dot={false}
          />
          {/* connectNulls: sparse rank data would otherwise leave lone days unplotted. */}
          <Line
            yAxisId="rank"
            dataKey="salesRank"
            stroke="var(--color-salesRank)"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            connectNulls
          />
          <MetadataEventOverlay
            buckets={chartData}
            events={events}
            granularity={granularity}
            onDrill={handleDrill}
          />
        </ComposedChart>
      </ChartContainer>
    </AmazonSalesChartPanel>
  );
};
